import React, { useCallback, useEffect, useState } from 'react';
import TextEditor from '../../../layout/ReusableComponent/Text-editor/TextEditor';
import {
  composePayloadIsEmpty,
  messageBodyFromEditorPayload,
} from './messagingEditorUtils';

function useDesktopMessagingComposer() {
  const [isDesktop, setIsDesktop] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(min-width: 961px)').matches
  );

  useEffect(() => {
    const mq = window.matchMedia('(min-width: 961px)');
    const onChange = () => setIsDesktop(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  return isDesktop;
}

/**
 * Message thread composer — reuses platform TextEditor.
 */
export default function MessagingComposeEditor({
  canSend = true,
  sending = false,
  disabledPlaceholder = 'Follow each other to continue messaging',
  onSend,
  editorKey = 0,
  hideToolbar = false,
  hideHint = false,
  inlineRowLayout = false,
}) {
  const isDesktop = useDesktopMessagingComposer();
  const expandComposerOnFocus = isDesktop || inlineRowLayout;
  const [payload, setPayload] = useState(null);

  const handleSend = useCallback(() => {
    if (!canSend || sending || composePayloadIsEmpty(payload)) return;
    const body = messageBodyFromEditorPayload(payload);
    if (!body) return;
    onSend?.(body);
  }, [canSend, sending, payload, onSend]);

  const placeholder = canSend ? 'Write a message…' : disabledPlaceholder;
  const isEmpty = composePayloadIsEmpty(payload);

  return (
    <div className="messaging-compose">
      <TextEditor
        key={editorKey}
        className="messaging-compose__editor"
        showContentFlags={false}
        composeExpandOnFocus={expandComposerOnFocus}
        defaultToolbarVisible={false}
        autoMobileExpandOnFocus={false}
        richPlaceholder={placeholder}
        onChange={setPayload}
        primaryActionText={sending ? 'Sending…' : 'Send'}
        onPrimaryAction={handleSend}
        primaryActionDisabled={!canSend || sending || isEmpty}
        primaryActionsOnRight
        primaryActionClassName="messaging-compose__send-btn"
        submitOnModifierEnter
        visible={{
          toolbar: false,
          flag: false,
          secondaryAction: false,
          otherButtons: true,
        }}
      />
      {!hideHint ? <p className="messaging-compose__hint">Press Ctrl+Enter (⌘+Enter on Mac) to send</p> : null}
    </div>
  );
}
